import { useMutation, UseMutationOptions } from '@tanstack/react-query';

import {
  payCalendarsService,
  DeletePayCalendarRequest,
  useToast,
} from '@aslanpay/portal-common-lib';

import { getPayCalendarErrorDescription, PayCalendarBatchError } from 'app/utils/payCalendars';

const useDeletePayCalendars = ({
  onSuccess,
  onError,
}: UseMutationOptions<unknown, Error, DeletePayCalendarRequest[]>) => {
  const toast = useToast();

  return useMutation<unknown, Error, DeletePayCalendarRequest[]>(
    async (ids: DeletePayCalendarRequest[]) => {
      const deletePromises = ids.map(({ id }) => payCalendarsService.deletePayCalendar(id));
      const results = await Promise.allSettled(deletePromises);

      const failures = results.filter(
        (result): result is PromiseRejectedResult => result.status === 'rejected'
      );
      const allSuccess = failures.length === 0;
      const allFailed = failures.length === results.length;

      const description = ids.length > 1 ? 'pay calendars were' : 'pay calendar was';

      if (allSuccess) {
        toast({
          position: 'top-right',
          status: 'success',
          title: `Pay calendar${ids.length > 1 ? 's' : ''} deleted`,
          description: `${ids.length} ${description} successfully deleted!`,
        });
      } else if (allFailed) {
        throw new PayCalendarBatchError(failures.map((failure) => failure.reason));
      } else {
        toast({
          position: 'top-right',
          status: 'warning',
          title: 'Some pay calendars were not deleted',
          description: getPayCalendarErrorDescription(
            failures.map((failure) => failure.reason),
            'Sorry, some of the pay calendars were not deleted. Please try again.'
          ),
        });
      }
    },
    {
      onSuccess: (data, variables, context) => {
        if (onSuccess) onSuccess(data, variables, context);
      },
      onError: (error, variables, context) => {
        const description = variables.length > 1 ? 'pay calendars were' : 'pay calendar was';
        const reasons = error instanceof PayCalendarBatchError ? error.reasons : [error];

        toast({
          position: 'top-right',
          status: 'error',
          title: 'Something went wrong',
          description: getPayCalendarErrorDescription(
            reasons,
            `Sorry, the ${description} not deleted. Please try again.`
          ),
        });

        if (onError) onError(error, variables, context);
      },
    }
  );
};

export default useDeletePayCalendars;
